/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import moment from 'moment'
import { parseInt } from 'lodash'

import type { DeploymentResources } from '@modules/cloud-api/v1/types'
import type { AnyClusterPlanInfo, AnyResourceInfo, SliderInstanceType } from '@modules/ui-types'

import { _getPlanInfoFromHistory, getPlanInfo, isSizedPlan } from './fundamentals'

type WithResources = {
  resources: DeploymentResources
}

export type PlanAttemptErrorDetails = { [key: string]: string }

export type ParsedPlanAttemptError = {
  message: string
  details: PlanAttemptErrorDetails
  errorType?: string
}

export function isPendingAttempt({ planAttempt }: { planAttempt: AnyClusterPlanInfo }): boolean {
  return !planAttempt.attempt_end_time
}

export function isSuccessfulShutdownAttempt({
  planAttempt,
}: {
  planAttempt: AnyClusterPlanInfo
}): boolean {
  return (
    // successfully
    // that completed...
    planAttempt.source?.action === `deployments.shutdown-deployment` && // a shutdown plan...
    !isPendingAttempt({ planAttempt }) &&
    planAttempt.healthy
  )
}

export function getDeploymentPlanAttemptId({
  deployment,
  sliderInstanceType,
  planAttempt,
}: {
  deployment: WithResources
  sliderInstanceType: SliderInstanceType
  planAttempt: AnyClusterPlanInfo
}): number | null {
  const { resources } = deployment
  const [resource] = resources[sliderInstanceType]

  if (!resource) {
    return null
  }

  return getPlanAttemptId({
    resource,
    planAttempt,
  })
}

export function getPlanAttemptId({
  resource,
  planAttempt,
}: {
  resource: AnyResourceInfo
  planAttempt: AnyClusterPlanInfo
}): number | null {
  const { plan_attempt_name: historyName } = planAttempt

  const historyNamePart = historyName?.split('-')[1]

  if (historyNamePart) {
    const historyNumber = parseInt(historyNamePart, 10)

    if (!isNaN(historyNumber)) {
      return historyNumber
    }
  }

  const {
    history,
    pending: pendingPlanAttempt,
    current: currentPlanAttempt,
  }: {
    history: AnyClusterPlanInfo[]
    pending?: AnyClusterPlanInfo
    current?: AnyClusterPlanInfo
  } = resource.info.plan_info

  if (planAttempt === pendingPlanAttempt || planAttempt === currentPlanAttempt) {
    const historyNumbers = history
      .map(({ plan_attempt_name: name }) => {
        const namePart = name?.split('-')[1]

        if (namePart) {
          return parseInt(namePart, 10)
        }

        return 0
      })
      .filter((number) => !isNaN(number))

    const maxHistoryNumber = Math.max(...historyNumbers)

    if (maxHistoryNumber >= 0) {
      return maxHistoryNumber + 1
    }

    return history.length
  }

  const historyIndex = history.indexOf(planAttempt)

  if (historyIndex !== -1) {
    return historyIndex
  }

  return null
}

export function getPlanBeforeAttempt({
  resource,
  planAttempt,
}: {
  resource: AnyResourceInfo
  planAttempt: AnyClusterPlanInfo
}) {
  const planInfo = resource.info.plan_info

  if (planAttempt === planInfo.pending) {
    return (planInfo.current && planInfo.current.plan) || null
  }

  function happenedBefore(otherPlanInfo: AnyClusterPlanInfo): boolean {
    if (planAttempt.attempt_start_time === undefined) {
      return true
    }

    return (
      otherPlanInfo.attempt_start_time !== undefined &&
      moment(otherPlanInfo.attempt_start_time) < moment(planAttempt.attempt_start_time)
    )
  }

  const prevSuccess = _getPlanInfoFromHistory({ resource, mustMatch: happenedBefore })
  return (prevSuccess && prevSuccess.plan) || null
}

export function getLastSizedPlanAttempt<TPlanInfo = AnyClusterPlanInfo>({
  deployment,
  sliderInstanceType,
}: {
  deployment: WithResources
  sliderInstanceType: SliderInstanceType
}): TPlanInfo | null {
  const { resources } = deployment
  const [resource] = resources[sliderInstanceType] as AnyResourceInfo[]

  if (!resource) {
    return null
  }

  const planInfo = _getPlanInfoFromHistory({ resource, mustMatch: hasSizedPlan })

  if (!planInfo) {
    return null
  }

  return planInfo as TPlanInfo

  function hasSizedPlan(anyPlanInfo: AnyClusterPlanInfo): boolean {
    return !!anyPlanInfo.plan && isSizedPlan(anyPlanInfo.plan)
  }
}

export function getLastPlanAttempt<TPlanInfo = AnyClusterPlanInfo>({
  deployment,
  sliderInstanceType,
}: {
  deployment: WithResources
  sliderInstanceType: SliderInstanceType
}): TPlanInfo | null {
  const { resources } = deployment
  const [resource] = resources[sliderInstanceType]

  if (!resource) {
    return null
  }

  const planInfo = getPlanInfo({ resource, state: `last_attempt` })
  return planInfo as any
}

export function parsePlanAttemptError({
  planAttempt,
}: {
  planAttempt: AnyClusterPlanInfo
}): ParsedPlanAttemptError | null {
  const { healthy, error } = planAttempt

  if (healthy || !error) {
    return null
  }

  return { message: error.message, details: error.details, errorType: error.failure_type }
}
